/**
 * ProfessionalFields — composable section for step 2.
 */

import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChipInput } from "./ChipInput";

export const NICHES = [
  "medicina",
  "odontologia",
  "psicologia",
  "nutrição",
  "fisioterapia",
  "fonoaudiologia",
  "terapia ocupacional",
  "biomedicina",
  "enfermagem",
  "farmácia",
  "outro",
] as const;

export const REGISTRY_TYPES = ["CRM", "CRO", "CRP", "CRN", "CREFITO", "Outro"] as const;

export const professionalFieldsSchema = z.object({
  niche: z.enum(NICHES, { required_error: "Escolha um nicho" }),
  formations: z.array(z.string().min(2)).default([]),
  registry_type: z.enum(REGISTRY_TYPES, { required_error: "Escolha o conselho" }),
  registry_number: z
    .string()
    .trim()
    .min(3, "Mínimo 3 caracteres")
    .regex(/[\d-]/, "Formato inválido"),
  years_active: z.coerce.number().int().min(0).max(70),
  clients_served_estimate: z.coerce.number().int().min(0).optional().nullable(),
});

export type ProfessionalFieldsValues = z.infer<typeof professionalFieldsSchema>;

const LABELS: Record<keyof ProfessionalFieldsValues, string> = {
  niche: "nicho",
  formations: "formações",
  registry_type: "conselho",
  registry_number: "número do conselho",
  years_active: "anos de atuação",
  clients_served_estimate: "clientes atendidos",
};

export interface ProfessionalFieldsProps {
  defaultValues?: Partial<ProfessionalFieldsValues>;
  onChange?: (patch: Partial<ProfessionalFieldsValues>) => void;
  onValidityChange?: (valid: boolean) => void;
  onMissingChange?: (labels: string[]) => void;
  registerForm?: (form: ReturnType<typeof useForm<ProfessionalFieldsValues>>) => void;
  validateOnMount?: boolean;
}

export function ProfessionalFields({
  defaultValues,
  onChange,
  onValidityChange,
  onMissingChange,
  registerForm,
  validateOnMount = false,
}: ProfessionalFieldsProps) {
  const form = useForm<ProfessionalFieldsValues>({
    resolver: zodResolver(professionalFieldsSchema),
    mode: "onChange",
    defaultValues: {
      niche: defaultValues?.niche,
      formations: defaultValues?.formations ?? [],
      registry_type: defaultValues?.registry_type,
      registry_number: defaultValues?.registry_number ?? "",
      years_active: defaultValues?.years_active,
      clients_served_estimate: defaultValues?.clients_served_estimate ?? null,
    },
  });

  useEffect(() => {
    registerForm?.(form);
  }, [form, registerForm]);

  useEffect(() => {
    onValidityChange?.(form.formState.isValid);
  }, [form.formState.isValid, onValidityChange]);

  useEffect(() => {
    if (validateOnMount) void form.trigger();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!onMissingChange) return;
    const errs = form.formState.errors;
    const labels = (Object.keys(errs) as Array<keyof ProfessionalFieldsValues>)
      .map((k) => LABELS[k])
      .filter(Boolean);
    onMissingChange(labels);
  }, [form.formState.errors, onMissingChange]);

  const { control, register, formState } = form;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Nicho</Label>
        <Controller
          name="niche"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value ?? ""}
              onValueChange={(v) => {
                field.onChange(v);
                onChange?.({ niche: v as ProfessionalFieldsValues["niche"] });
              }}
            >
              <SelectTrigger aria-label="Nicho">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {NICHES.map((n) => (
                  <SelectItem key={n} value={n}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div className="space-y-2">
        <Label>Formações (opcional)</Label>
        <Controller
          name="formations"
          control={control}
          render={({ field }) => (
            <ChipInput
              value={field.value ?? []}
              onChange={(next) => {
                field.onChange(next);
                onChange?.({ formations: next });
              }}
              placeholder="Ex: Residência em Ortopedia"
              ariaLabel="Formações"
            />
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Conselho</Label>
          <Controller
            name="registry_type"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value ?? ""}
                onValueChange={(v) => {
                  field.onChange(v);
                  onChange?.({
                    registry_type: v as ProfessionalFieldsValues["registry_type"],
                  });
                }}
              >
                <SelectTrigger aria-label="Conselho">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  {REGISTRY_TYPES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ob-registry-number">Número</Label>
          <Input
            id="ob-registry-number"
            placeholder="Ex: 12345-SP"
            {...register("registry_number", {
              onBlur: (e) => onChange?.({ registry_number: e.target.value }),
              onChange: (e) => onChange?.({ registry_number: e.target.value }),
            })}
          />
          {formState.errors.registry_number ? (
            <p className="text-xs text-destructive">{formState.errors.registry_number.message}</p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="ob-years">Anos de atuação</Label>
          <Input
            id="ob-years"
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            min={0}
            max={70}
            {...register("years_active", {
              valueAsNumber: true,
              onChange: (e) => {
                const n = Number(e.target.value);
                if (!Number.isNaN(n)) onChange?.({ years_active: n });
              },
            })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ob-clients">Clientes atendidos</Label>
          <Input
            id="ob-clients"
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            min={0}
            placeholder="estimativa"
            {...register("clients_served_estimate", {
              valueAsNumber: true,
              onChange: (e) => {
                const n = Number(e.target.value);
                onChange?.({
                  clients_served_estimate: Number.isNaN(n) ? null : n,
                });
              },
            })}
          />
          <p className="text-xs italic text-muted-foreground">estimativa, sem pressão</p>
        </div>
      </div>
    </div>
  );
}
